import { z } from 'zod';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { ApiError } from './api';

/**
 * Cliente HTTP + hooks para `/api/biometrics/*` (M2.32.A).
 *
 * Sigue el mismo patrón que `lib/load.ts` y `lib/dashboard.ts`:
 *   - Schemas Zod source-of-truth.
 *   - `getJson` / `putJson` propios para mantener mapeo de errores local.
 *   - Hooks TanStack Query agrupados bajo `['biometrics', ...]`.
 *
 * Endpoints expuestos:
 *   - useBiometrics(days?)                → lista DESC últimas N entradas.
 *   - useTodayBiometrics()                → entrada de hoy (TZ del profile) o null.
 *   - useReadiness(dateISO?)              → score + level del día (o hoy).
 *   - useUpsertBiometrics()               → mutación PUT con invalidación automática.
 */

const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

export const READINESS_LEVELS = ['green', 'yellow', 'red'] as const;
export type ReadinessLevel = (typeof READINESS_LEVELS)[number];

export const BiometricEntrySchema = z.object({
  dateISO: z.string(),
  rhrBpm: z.number().int().nullable(),
  hrvRmssdMs: z.number().int().nullable(),
  sleepHours: z.number().nullable(),
  sleepQuality: z.number().int().nullable(),
  notes: z.string().nullable(),
  updatedAt: z.string(),
});
export type BiometricEntry = z.infer<typeof BiometricEntrySchema>;

export const BiometricsListResponseSchema = z.object({
  items: z.array(BiometricEntrySchema),
  days: z.number().int().positive(),
});
export type BiometricsListResponse = z.infer<typeof BiometricsListResponseSchema>;

export const BiometricsTodayResponseSchema = z.object({
  dateISO: z.string(),
  item: BiometricEntrySchema.nullable(),
});
export type BiometricsTodayResponse = z.infer<typeof BiometricsTodayResponseSchema>;

export const ReadinessUnavailableSchema = z.object({
  dateISO: z.string(),
  available: z.literal(false),
  reason: z.string().optional(),
});

export const ReadinessAvailableSchema = z.object({
  dateISO: z.string(),
  available: z.literal(true),
  score: z.number().int().min(0).max(100),
  level: z.enum(READINESS_LEVELS),
  reasons: z.array(z.string()),
  rolling7d: z.number().int().nullable(),
  rhrToday: z.number().int().nullable(),
  hrvToday: z.number().int().nullable(),
  rhrBaseline: z.number().int().nullable(),
  hrvBaseline: z.number().int().nullable(),
  hrvDeviationSigma: z.number().nullable(),
  rhrDeviationSigma: z.number().nullable(),
  sleepDeficitHours3d: z.number(),
});

export const ReadinessResponseSchema = z.discriminatedUnion('available', [
  ReadinessAvailableSchema,
  ReadinessUnavailableSchema,
]);
export type ReadinessResponse = z.infer<typeof ReadinessResponseSchema>;
export type ReadinessAvailable = z.infer<typeof ReadinessAvailableSchema>;

export const BiometricsUpsertBodySchema = z.object({
  rhrBpm: z.number().int().min(30).max(120).nullable().optional(),
  hrvRmssdMs: z.number().int().min(5).max(200).nullable().optional(),
  sleepHours: z.number().min(0).max(14).nullable().optional(),
  sleepQuality: z.number().int().min(1).max(10).nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});
export type BiometricsUpsertBody = z.infer<typeof BiometricsUpsertBodySchema>;

export const BiometricsUpsertResponseSchema = z.object({
  item: BiometricEntrySchema,
});
export type BiometricsUpsertResponse = z.infer<typeof BiometricsUpsertResponseSchema>;

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function getJson<T>(path: string, schema: z.ZodType<T>): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    throw new ApiError(`Respuesta no OK del backend: ${response.status}`, response.status);
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

async function putJson<T>(
  path: string,
  schema: z.ZodType<T>,
  body: unknown,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body ?? {}),
    });
  } catch (err) {
    throw new ApiError('Error de red al contactar con el backend', 0, err);
  }
  if (!response.ok) {
    let detail: unknown;
    try {
      detail = await response.json();
    } catch {
      detail = null;
    }
    throw new ApiError(
      `Respuesta no OK del backend: ${response.status}`,
      response.status,
      detail,
    );
  }
  const json = await readJsonSafe(response);
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(`Respuesta ${path} no cumple esquema`, response.status, parsed.error);
  }
  return parsed.data;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export function getBiometrics(days = 30): Promise<BiometricsListResponse> {
  return getJson(
    `/api/biometrics?days=${encodeURIComponent(String(days))}`,
    BiometricsListResponseSchema,
  );
}

export function getTodayBiometrics(): Promise<BiometricsTodayResponse> {
  return getJson('/api/biometrics/today', BiometricsTodayResponseSchema);
}

export function getReadiness(dateISO?: string): Promise<ReadinessResponse> {
  const qs = dateISO ? `?dateISO=${encodeURIComponent(dateISO)}` : '';
  return getJson(`/api/biometrics/readiness${qs}`, ReadinessResponseSchema);
}

export function upsertBiometrics(
  dateISO: string,
  body: BiometricsUpsertBody,
): Promise<BiometricsUpsertResponse> {
  return putJson(
    `/api/biometrics/${encodeURIComponent(dateISO)}`,
    BiometricsUpsertResponseSchema,
    body,
  );
}

// ---------------------------------------------------------------------------
// Query keys + hooks
// ---------------------------------------------------------------------------

export const BIOMETRICS_QUERY_KEY = ['biometrics'] as const;
export const BIOMETRICS_LIST_QUERY_KEY = ['biometrics', 'list'] as const;
export const BIOMETRICS_TODAY_QUERY_KEY = ['biometrics', 'today'] as const;
export const BIOMETRICS_READINESS_QUERY_KEY = ['biometrics', 'readiness'] as const;

export function useBiometrics(
  days = 30,
): UseQueryResult<BiometricsListResponse, Error> {
  return useQuery<BiometricsListResponse, Error>({
    queryKey: [...BIOMETRICS_LIST_QUERY_KEY, days],
    queryFn: () => getBiometrics(days),
    staleTime: 30_000,
    retry: 1,
  });
}

export function useTodayBiometrics(): UseQueryResult<BiometricsTodayResponse, Error> {
  return useQuery<BiometricsTodayResponse, Error>({
    queryKey: BIOMETRICS_TODAY_QUERY_KEY,
    queryFn: getTodayBiometrics,
    staleTime: 30_000,
    retry: 1,
  });
}

export function useReadiness(
  dateISO?: string,
): UseQueryResult<ReadinessResponse, Error> {
  return useQuery<ReadinessResponse, Error>({
    queryKey: [...BIOMETRICS_READINESS_QUERY_KEY, dateISO ?? 'today'],
    queryFn: () => getReadiness(dateISO),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * Mutación PUT /api/biometrics/:dateISO. Tras éxito invalida toda la cache
 * de biometría (lista, today, readiness) para que la UI refleje el cambio.
 */
export function useUpsertBiometrics(): UseMutationResult<
  BiometricsUpsertResponse,
  Error,
  { dateISO: string; body: BiometricsUpsertBody }
> {
  const qc = useQueryClient();
  return useMutation<
    BiometricsUpsertResponse,
    Error,
    { dateISO: string; body: BiometricsUpsertBody }
  >({
    mutationFn: ({ dateISO, body }) => upsertBiometrics(dateISO, body),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: BIOMETRICS_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Helpers de presentación
// ---------------------------------------------------------------------------

/** Color tailwind asociado al nivel de readiness (border-color). */
export function readinessBorderClass(level: ReadinessLevel): string {
  switch (level) {
    case 'green':
      return 'border-emerald-300';
    case 'yellow':
      return 'border-amber-300';
    case 'red':
      return 'border-rose-300';
  }
}

/** Color tailwind para texto/pildoras del nivel. */
export function readinessTextClass(level: ReadinessLevel): string {
  switch (level) {
    case 'green':
      return 'text-emerald-800';
    case 'yellow':
      return 'text-amber-800';
    case 'red':
      return 'text-rose-800';
  }
}

/** Etiqueta legible del nivel en español. */
export function readinessLabel(level: ReadinessLevel): string {
  switch (level) {
    case 'green':
      return 'verde';
    case 'yellow':
      return 'ámbar';
    case 'red':
      return 'rojo';
  }
}
